import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';
import { Send, Loader2, MessageSquareText, Sparkles, User, Bot, Filter, Calendar, MapPin, Building2, Copy, Check, RotateCcw, Trash2, Maximize2, Minimize2, HelpCircle, Database, BarChart3, ChevronRight, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DataDictionary from '../components/DataDictionary';

const STORAGE_KEY = 'overtimeiq_chat_page_history';

const ChatPage = () => {
  const { API } = React.useContext(AppContext);
  const { filters, hasActiveFilters } = useFilters();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `session-${Date.now()}`);
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelper, setShowHelper] = useState(false);
  const [showDataDictionary, setShowDataDictionary] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Quick reference data for the helper panel
  const quickReference = {
    dimensions: ['Location', 'Department', 'Supervisor', 'Employee', 'Job Title', 'Pay Type', 'Date'],
    measures: ['Total Hours', 'Premium Hours', 'Non-Premium Hours', 'Premium %', 'Headcount', 'Estimated Cost'],
    exampleQuestions: [
      "Which department has the highest overtime?",
      "Show me the top 10 employees with excessive premium hours",
      "What's the overtime trend for the last 6 months?",
      "Compare overtime costs between locations",
      "Who are the supervisors with the most overtime on their teams?",
      "What would be the ROI of hiring 2 more staff in Housekeeping?"
    ]
  };

  // Default welcome message and suggestions
  const welcomeMessage = {
    role: 'assistant',
    content: "Hello! I'm your OvertimeIQ Assistant. I have access to your live timesheet data and can help you analyze overtime patterns, identify problem areas, and provide recommendations.\n\n**What I can help with:**\n- Analyze overtime by department, location, or manager\n- Identify employees with excessive overtime\n- Show trends and patterns over time\n- Provide hiring and scheduling recommendations\n\nWhat would you like to know?",
    suggestedQuestions: [
      "Which department has the highest overtime costs?",
      "Show me overtime trends by location",
      "Who are the top 10 employees with excessive overtime?",
      "Should we hire more staff to reduce overtime?"
    ],
    filterContext: null
  };

  // Load chat history from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setMessages(parsed);
          return;
        }
      }
    } catch (e) {
      // Non-critical: chat history is a convenience feature
    }
    // Set welcome message if no history
    setMessages([welcomeMessage]);
  }, []);

  // Save chat history to localStorage when messages change
  useEffect(() => {
    if (messages.length > 0) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
        // Also save to sessionStorage for export access
        sessionStorage.setItem('overtimeiq_chat_messages', JSON.stringify(messages));
      } catch (e) {
        // Non-critical: chat history persistence is a convenience feature
      }
    }
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Capture current filter context
  const captureFilterContext = useCallback(() => {
    return {
      location: filters?.location || null,
      department: filters?.department || null,
      manager: filters?.manager || null,
      start_date: filters?.start_date || null,
      end_date: filters?.end_date || null,
      pay_types: filters?.pay_types || null
    };
  }, [filters]);

  // Format filter context for display
  const formatFilterBadge = (filterContext) => {
    if (!filterContext) return null;
    const parts = [];
    if (filterContext.location) parts.push(filterContext.location);
    if (filterContext.department) parts.push(filterContext.department);
    if (filterContext.manager) parts.push(filterContext.manager);
    return parts.length > 0 ? parts.join(' • ') : null;
  };

  // Get current filter display
  const getCurrentFilterDisplay = () => {
    const parts = [];
    if (filters.location) parts.push(filters.location);
    if (filters.department) parts.push(filters.department);
    if (filters.manager) parts.push(filters.manager);
    return parts.length > 0 ? parts.join(' • ') : 'All Data';
  };

  const sendMessage = async (messageText) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;

    const userMessage = { 
      role: 'user', 
      content: text,
      timestamp: new Date().toISOString(),
      filterContext: captureFilterContext()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      // Include filters in the chat request
      const response = await axios.post(`${API}/chat`, {
        message: text,
        session_id: sessionId,
        filters: {
          start_date: filters.start_date,
          end_date: filters.end_date,
          location: filters.location,
          department: filters.department,
          manager: filters.manager,
          pay_types: filters.pay_types && filters.pay_types.length > 0 ? filters.pay_types : null
        }
      });

      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        suggestedQuestions: response.data.suggested_questions || generateFollowUps(text, response.data.response),
        timestamp: new Date().toISOString(),
        filterContext: captureFilterContext(),
        isError: response.data.error || false,
        errorInfo: response.data.error_info || null
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      
      // Enhanced error handling with categories
      let errorContent = '';
      let errorInfo = null;
      
      if (error.code === 'ERR_NETWORK') {
        errorInfo = {
          category: 'Connection Error',
          icon: '🔌',
          user_can_fix: false,
          error_key: 'connection'
        };
        errorContent = '**🔌 Connection Error**\n\nUnable to connect to the server.\n\n**What to do:** Check your internet connection. If the problem persists, contact your administrator.';
      } else if (error.response?.status === 429) {
        errorInfo = {
          category: 'Rate/Usage Limits',
          icon: '⏳',
          user_can_fix: false,
          error_key: 'rate_limit'
        };
        errorContent = '**⏳ Rate/Usage Limits**\n\nThe AI service is currently busy.\n\n**What to do:** Wait 30 seconds and try again. Check your API key balance in Emergent settings.';
      } else if (error.response?.status >= 500) {
        errorInfo = {
          category: 'Server Error',
          icon: '⚠️',
          user_can_fix: true,
          error_key: 'server_error'
        };
        errorContent = '**⚠️ Server Error**\n\nThe server encountered an error processing your request.\n\n**What to do:** Try a simpler question, or try again in a moment.';
      } else {
        errorInfo = {
          category: 'Unexpected Error',
          icon: '⚠️',
          user_can_fix: false,
          error_key: 'unknown'
        };
        errorContent = '**⚠️ Unexpected Error**\n\nSorry, I encountered an error.\n\n**What to do:** Please try again. If the problem persists, contact your administrator.';
      }
      
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: errorContent,
        suggestedQuestions: [
          "Which department has the highest overtime?",
          "Show me the top 10 employees by overtime",
          "What are the overtime trends?"
        ],
        timestamp: new Date().toISOString(),
        filterContext: captureFilterContext(),
        isError: true,
        errorInfo: errorInfo
      }]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  // Generate follow-up questions if backend doesn't provide them
  const generateFollowUps = (question, response) => {
    const followUps = [];
    const lowerQ = question.toLowerCase();
    const lowerR = response.toLowerCase();
    
    if (lowerQ.includes('department') || lowerR.includes('department')) {
      followUps.push('Which employees in this department have the most overtime?');
      followUps.push('What are the overtime trends for this department?');
    }
    if (lowerQ.includes('employee') || lowerR.includes('employee')) {
      followUps.push('What departments do these employees work in?');
      followUps.push('How does this compare to the company average?');
    }
    if (lowerQ.includes('cost') || lowerR.includes('cost')) {
      followUps.push('What would be the ROI of hiring additional staff?');
      followUps.push('Which areas have the highest cost per overtime hour?');
    }
    if (lowerQ.includes('trend') || lowerR.includes('trend')) {
      followUps.push('What factors are driving these trends?');
      followUps.push('How do weekends compare to weekdays?');
    }
    
    // Default follow-ups
    if (followUps.length === 0) {
      followUps.push('Can you break this down further?');
      followUps.push('What recommendations do you have?');
      followUps.push('How does this compare to last quarter?');
    }
    
    return followUps.slice(0, 4);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestedQuestion = (question) => {
    sendMessage(question);
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 2000);
    } catch (e) {
      console.error('Failed to copy:', e);
    }
  };

  const clearHistory = () => {
    if (window.confirm('Clear all chat history? This cannot be undone.')) {
      setMessages([welcomeMessage]);
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  const reaskWithCurrentFilters = (originalMessage) => {
    sendMessage(originalMessage);
  };

  // Check if filter context differs from current
  const filtersDiffer = (msgContext) => {
    if (!msgContext) return false;
    return msgContext.location !== filters.location ||
           msgContext.department !== filters.department ||
           msgContext.manager !== filters.manager;
  };

  return (
    <div className={`${isFullscreen ? 'fixed inset-0 z-50 bg-white' : 'h-[calc(100vh-2rem)]'} flex flex-col ${isFullscreen ? 'p-2' : 'p-4 md:p-6'}`} data-testid="chat-page">
      {/* Header - Compact in fullscreen */}
      {isFullscreen ? (
        <div className="flex justify-between items-center mb-2 px-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <MessageSquareText className="h-4 w-4" />
            <span className="font-medium">AI Chat</span>
            <span className="text-slate-400">•</span>
            <span className="text-xs">{filters.location || filters.department || 'All Data'}</span>
          </div>
          <div className="flex items-center gap-1">
            {messages.length > 1 && (
              <button
                onClick={clearHistory}
                className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                title="Clear chat history"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button
              onClick={() => setIsFullscreen(false)}
              className="p-1.5 text-slate-400 hover:text-[#2E5C8A] hover:bg-[#2E5C8A]/10 rounded transition-colors"
              title="Exit fullscreen"
            >
              <Minimize2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex justify-between items-start">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#1e3a5f] flex items-center gap-2">
                <MessageSquareText className="h-7 w-7" />
                AI Chat
              </h1>
              <p className="text-muted-foreground text-sm">Ask questions about your overtime data - powered by live analytics</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowHelper(!showHelper)}
                className={`p-2 rounded-lg transition-colors flex items-center gap-1.5 text-sm ${
                  showHelper 
                    ? 'bg-[#4CAF93] text-white' 
                    : 'text-slate-500 hover:text-[#4CAF93] hover:bg-[#4CAF93]/10'
                }`}
                title="What can I ask?"
                data-testid="helper-toggle"
              >
                <HelpCircle className="h-5 w-5" />
                <span className="hidden sm:inline">What can I ask?</span>
              </button>
              {messages.length > 1 && (
                <button
                  onClick={clearHistory}
                  className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                  title="Clear chat history"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
              <button
                onClick={() => setIsFullscreen(true)}
                className="p-2 text-slate-500 hover:text-[#2E5C8A] hover:bg-[#2E5C8A]/10 rounded-lg transition-colors"
                title="Fullscreen mode"
              >
                <Maximize2 className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Helper Panel - Collapsible */}
          <AnimatePresence>
            {showHelper && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="mb-4 overflow-hidden"
              >
                <div className="bg-gradient-to-r from-[#4CAF93]/10 to-[#2E5C8A]/10 rounded-xl border border-[#4CAF93]/20 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Dimensions */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="h-4 w-4 text-[#2E5C8A]" />
                        <span className="font-semibold text-sm text-slate-700">Dimensions</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {quickReference.dimensions.map(dim => (
                          <span 
                            key={dim}
                            className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-600"
                          >
                            {dim}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Measures */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <BarChart3 className="h-4 w-4 text-[#4CAF93]" />
                        <span className="font-semibold text-sm text-slate-700">Measures</span>
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {quickReference.measures.map(measure => (
                          <span 
                            key={measure}
                            className="text-xs bg-white px-2 py-1 rounded border border-slate-200 text-slate-600"
                          >
                            {measure}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Example Questions */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <Sparkles className="h-4 w-4 text-[#FF8C42]" />
                        <span className="font-semibold text-sm text-slate-700">Try asking...</span>
                      </div>
                      <div className="space-y-1">
                        {quickReference.exampleQuestions.slice(0, 3).map((question) => (
                          <button
                            key={question}
                            onClick={() => {
                              setShowHelper(false);
                              sendMessage(question);
                            }}
                            className="w-full text-left text-xs text-[#2E5C8A] hover:text-[#1e3a5f] hover:bg-white/50 px-2 py-1 rounded transition-colors flex items-center gap-1"
                          >
                            <ChevronRight className="h-3 w-3 flex-shrink-0" />
                            <span className="truncate">{question}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Link to full Data Dictionary */}
                  <div className="mt-3 pt-3 border-t border-slate-200 flex justify-center">
                    <button
                      onClick={() => setShowDataDictionary(true)}
                      className="text-sm text-[#2E5C8A] hover:text-[#1e3a5f] font-medium flex items-center gap-2 px-4 py-2 rounded-lg hover:bg-white/50 transition-colors"
                    >
                      <BookOpen className="h-4 w-4" />
                      View Full Data Dictionary (with formulas)
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Active Filters Indicator - Only in normal mode */}
          <div className="mb-3 bg-slate-50 rounded-lg p-3 border border-slate-200" data-testid="chat-filters-indicator">
            <div className="flex items-center gap-2 text-sm">
              <Filter className="h-4 w-4 text-[#2E5C8A]" />
              <span className="font-medium text-slate-700">Current Context:</span>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-white px-2 py-1 rounded border border-slate-200 text-xs">
                  <Calendar className="h-3 w-3 text-slate-500" />
                  {filters.start_date} to {filters.end_date}
                </span>
                {filters.location && (
                  <span className="inline-flex items-center gap-1 bg-[#FF8C42]/10 text-[#FF8C42] px-2 py-1 rounded border border-[#FF8C42]/20 text-xs font-medium">
                    <MapPin className="h-3 w-3" />
                    {filters.location}
                  </span>
                )}
                {filters.department && (
                  <span className="inline-flex items-center gap-1 bg-[#2E5C8A]/10 text-[#2E5C8A] px-2 py-1 rounded border border-[#2E5C8A]/20 text-xs font-medium">
                    <Building2 className="h-3 w-3" />
                    {filters.department}
                  </span>
                )}
                {filters.pay_types && filters.pay_types.length > 0 && (
                  <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2 py-1 rounded border border-green-200 text-xs font-medium">
                    {filters.pay_types.length === 1 ? filters.pay_types[0] : `${filters.pay_types.length} Pay Types`}
                  </span>
                )}
                {!hasActiveFilters && !filters.location && !filters.department && (!filters.pay_types || filters.pay_types.length === 0) && (
                  <span className="text-xs text-slate-500">All locations, departments & pay types</span>
                )}
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-1">AI responses are filtered to this context. Chat history persists across filter changes.</p>
          </div>
        </>
      )}

      {/* Chat Container */}
      <div className="flex-1 bg-white rounded-xl border border-border shadow-sm flex flex-col overflow-hidden">
        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-6" data-testid="chat-messages">
          <AnimatePresence>
            {messages.map((message, index) => (
              <motion.div
                key={message.timestamp || `msg-${index}`}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {message.role === 'assistant' && (
                  <div className="w-8 h-8 rounded-full bg-[#2E5C8A] flex items-center justify-center flex-shrink-0">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                )}
                
                <div className={`max-w-[80%] ${message.role === 'user' ? 'order-first' : ''}`}>
                  {/* Filter context badge for user messages */}
                  {message.role === 'user' && message.filterContext && (
                    <div className="flex items-center gap-1 mb-1 text-[10px] text-slate-400 justify-end">
                      <Filter size={10} />
                      <span>{formatFilterBadge(message.filterContext) || 'All Data'}</span>
                      {filtersDiffer(message.filterContext) && (
                        <button
                          onClick={() => reaskWithCurrentFilters(message.content)}
                          className="ml-1 text-[#2E5C8A] hover:underline flex items-center gap-0.5"
                          title="Re-ask with current filters"
                        >
                          <RotateCcw size={10} />
                          <span>Re-ask</span>
                        </button>
                      )}
                    </div>
                  )}

                  <div
                    className={`relative group rounded-2xl px-4 py-3 ${
                      message.role === 'user'
                        ? 'bg-[#1e3a5f] text-white rounded-br-md'
                        : message.isError
                          ? message.errorInfo?.user_can_fix
                            ? 'bg-amber-50 border border-amber-200 text-foreground rounded-bl-md'
                            : 'bg-red-50 border border-red-200 text-foreground rounded-bl-md'
                          : 'bg-slate-100 text-foreground rounded-bl-md'
                    }`}
                  >
                    {message.role === 'assistant' ? (
                      <div className={`prose prose-sm max-w-none ${
                        message.isError 
                          ? message.errorInfo?.user_can_fix
                            ? 'prose-headings:text-amber-800 prose-strong:text-amber-800'
                            : 'prose-headings:text-red-800 prose-strong:text-red-800'
                          : 'prose-headings:text-[#1e3a5f] prose-strong:text-[#1e3a5f]'
                      } prose-headings:font-semibold prose-headings:mt-4 prose-headings:mb-2 prose-p:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5 prose-table:text-sm [&_table]:w-full [&_table]:border-collapse [&_table]:my-3 [&_th]:bg-slate-200 [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_th]:border [&_th]:border-slate-300 [&_td]:px-3 [&_td]:py-2 [&_td]:border [&_td]:border-slate-200 [&_tr:nth-child(even)]:bg-slate-50 [&_hr]:my-4 [&_hr]:border-slate-200`}>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                        {/* Error category badge */}
                        {message.isError && message.errorInfo && (
                          <div className={`mt-3 pt-2 border-t ${message.errorInfo.user_can_fix ? 'border-amber-200' : 'border-red-200'}`}>
                            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full ${
                              message.errorInfo.user_can_fix 
                                ? 'bg-amber-100 text-amber-700' 
                                : 'bg-red-100 text-red-700'
                            }`}>
                              {message.errorInfo.user_can_fix ? '✏️ You can fix this' : '🔧 Admin action needed'}
                            </span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p>{message.content}</p>
                    )}

                    {/* Copy button */}
                    {message.role === 'assistant' && (
                      <button
                        onClick={() => copyToClipboard(message.content, index)}
                        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1.5 hover:bg-slate-200 rounded"
                        title="Copy response"
                      >
                        {copiedIndex === index ? (
                          <Check size={14} className="text-green-600" />
                        ) : (
                          <Copy size={14} className="text-slate-400" />
                        )}
                      </button>
                    )}
                  </div>
                  
                  {/* Follow-up Suggestions - show only for last assistant message */}
                  {message.role === 'assistant' && message.suggestedQuestions?.length > 0 && index === messages.length - 1 && (
                    <div className="mt-3">
                      <p className="text-xs text-slate-500 font-medium mb-2 flex items-center gap-1">
                        <Sparkles className="h-3 w-3" />
                        Follow-up questions:
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {message.suggestedQuestions.map((question) => (
                          <button
                            key={question}
                            onClick={() => handleSuggestedQuestion(question)}
                            className="text-xs px-3 py-1.5 bg-white border border-[#2E5C8A]/30 text-[#2E5C8A] rounded-full hover:bg-[#2E5C8A]/10 transition-colors flex items-center gap-1"
                            data-testid={`suggested-question-${question.slice(0, 20).replace(/\s/g, '-')}`}
                          >
                            <Sparkles className="h-3 w-3" />
                            {question}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {message.role === 'user' && (
                  <div className="w-8 h-8 rounded-full bg-[#FF8C42] flex items-center justify-center flex-shrink-0">
                    <User className="h-4 w-4 text-white" />
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

          {isLoading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex gap-3"
            >
              <div className="w-8 h-8 rounded-full bg-[#2E5C8A] flex items-center justify-center">
                <Bot className="h-4 w-4 text-white" />
              </div>
              <div className="bg-slate-100 rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Analyzing your data...</span>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area - Compact in fullscreen */}
        <div className={`border-t border-border ${isFullscreen ? 'p-2' : 'p-4'} bg-slate-50`}>
          <div className="flex gap-2 items-end">
            <textarea
              ref={inputRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={isFullscreen ? "Ask a question..." : "Ask about overtime patterns, costs, or recommendations..."}
              className={`flex-1 resize-none rounded-xl border border-border px-4 ${isFullscreen ? 'py-2 min-h-[40px]' : 'py-3 min-h-[50px]'} focus:outline-none focus:ring-2 focus:ring-[#2E5C8A] focus:border-transparent max-h-[150px] bg-white`}
              rows={1}
              data-testid="chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!input.trim() || isLoading}
              className={`${isFullscreen ? 'h-[40px] px-4' : 'h-[50px] px-5'} bg-[#2E5C8A] text-white rounded-xl hover:bg-[#1e3a5f] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2`}
              data-testid="send-message-button"
            >
              {isLoading ? (
                <Loader2 className={`${isFullscreen ? 'h-4 w-4' : 'h-5 w-5'} animate-spin`} />
              ) : (
                <>
                  <Send className={isFullscreen ? 'h-4 w-4' : 'h-5 w-5'} />
                  {!isFullscreen && <span className="hidden sm:inline">Send</span>}
                </>
              )}
            </button>
          </div>
          {!isFullscreen && (
            <p className="text-xs text-muted-foreground mt-2 text-center">
              Chat history is saved locally and persists across filter changes
            </p>
          )}
        </div>
      </div>

      {/* Data Dictionary Modal */}
      <DataDictionary 
        isOpen={showDataDictionary} 
        onClose={() => setShowDataDictionary(false)} 
      />
    </div>
  );
};

export default ChatPage;
